<template>
  <ion-page id="legacy-rep-import-page">
    <ion-content>

      <div v-show="showTour">
        <Tour  :tour="tourInfo" :tourTrigger="tourTrigger" @tour-done="tourDone" />
      </div>

      <div v-show="showState == SHOW_IMPORT" id="filepond">
        
        <h3>{{ t("Import PGN files containing Repertoires") }}</h3>
        <!-- accepted-file-types="text/plain, text/csv, text/pgn" -->

        <!-- //           server="https://www.chessvis.com/web_download.php"
        // server="http://chessvis.ddns.net:8888//web_download.php" -->

        <file-pond
          name="test"
          ref="pond"
          :label-idle="labelIdleString"
          v-bind:allow-multiple="true"
          :allow-revert="false"
          server="https://www.chessvis.com/chessvis_web_download.php"
          v-on:init="handleFilePondInit"
        />
      </div>

      <div id="the_results" v-show="showState == SHOW_RESULTS" class="import-history-container" >
        <div class="import-history-row">
          <div class="import-history-text import-history-source import-history-heading">{{ t("Source") }}</div>
          <div class="import-history-text import-history-left  import-history-heading">{{ t("Your Filename") }}</div>
          <div class="import-history-text import-history-left import-history-heading">{{ t("Date") }}</div>
          <div class="import-history-text import-history-count import-history-heading">{{ t("Rep#") }}"</div>
        </div>
        <div v-for="item in importHistory" v-bind:key="item.date">
          <div class="import-history-row" v-bind:class="hoverClassObject" @click="onClickImport(item.index)">
            <div class="import-history-text import-history-source">{{ item.source }}</div>
            <div class="import-history-text import-history-left">{{ item.filename }}</div>
            <div class="import-history-text import-history-left">{{ item.date }}</div>
            <div class="import-history-text import-history-count">{{ item.count }}</div>
          </div>
        </div>
      </div>

      <!-- taken from rep import view -->
      <div v-show="showState == SHOW_REPMANAGER" >
        <LegacyRepManager :downloadID="downloadID" whoCalled=import :descriptionHeading="descriptionHeadingString" :key="downloadID" 
                @repertoire-manager-done="repertoireManagerDone" />
      </div>
 
    </ion-content>
    <Toolbar id="the_toolbar" :ignoreClicks="showTour" :elements="toolbarElements" 
                  :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
  </ion-page>
</template>
    
<script setup>
  
  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';

  //  https://pqina.nl/filepond/docs/getting-started/installation/vue/
  
  // npm install vue-filepond filepond
  
  // about adding metadata
  // https://github.com/pqina/vue-filepond/issues/57
  
  // npm i filepond-plugin-image-preview
  // npm i filepond-plugin-file-validate-type
  // npm i filepond-plugin-file-metadata
  
  // Import Vue FilePond
  import vueFilePond from "vue-filepond";
  import { setOptions } from 'vue-filepond';
  
  // Import FilePond styles
  import "filepond/dist/filepond.min.css";
  
  // Import FilePond plugins
  // Please note that you need to install these plugins separately
  
  // Import image preview plugin styles
  import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
  
  // Import image preview and file type validation plugins
  import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
  import FilePondPluginImagePreview from "filepond-plugin-image-preview";
  import FilePondPluginFileMetadata from 'filepond-plugin-file-metadata'
  
  // Chessvis stuff
  import { t } from '@/composables/translateText.js'

  import Toolbar from '@/components/Toolbar.vue'
  import LegacyRepTree from '@/components/LegacyRepTree.vue'

  import { ref, watch } from 'vue'
  import { useRouter } from 'vue-router';
  import { onMounted, onBeforeUnmount } from 'vue'
  import { useGetData } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useStringStore } from '@/stores/stringStore';

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect } from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import Tour from '@/components/Tour.vue'
  import LegacyRepManager from '@/components/LegacyRepManager.vue';

  const router = useRouter()

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })

  const connectionStore = useConnectionStore()
  const stringStore = useStringStore()

  const isDark = ref(connectionStore.getDarkmode != "light")

  const labelIdleString = ref(t("Drop PGN files with Repertoires here") + "...")
  const descriptionHeadingString = ref(t("Repertoires in Download"))
  const menuTrigger = ref(0)
  const tourTrigger = ref(0)
  const showTour = ref(false)

  const TOOLBAR_IMPORT = 0
  const TOOLBAR_RESULTS = 1
  const TOOLBAR_TOUR = 2
  const TOOLBAR_DONE = 3

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const toolbarElements = ref([
    { index : TOOLBAR_IMPORT, label : t("Import") ,disabled : true},
    { index : TOOLBAR_RESULTS, label : t("Results") , disabled : false},
  ])

  const SHOW_IMPORT = 0
  const SHOW_RESULTS = 1
  const SHOW_TREE = 2
  const SHOW_REPMANAGER = 3
  const SHOW_SUBSCRIBERS_ONLY = 5

  const showState = ref(SHOW_IMPORT)

  const hoverClassObject = ref((isDark.value) ? "dark-hover-mode" : "light-hover-mode")
  const toolbarTrigger = ref(0)
  const importHistory = ref([])
  const downloadID = ref("0")

  // tour stuff
  const tourInfo = ref({
    id : "repertoire_import",
    video : "https://www.chessvis.com/videos/repertoires_on_web.mp4",
    elements : [
      { id: "", title: "Repertoire Import", text : "To import repertoires built elswehere, just drag and drop them here.<br><br>The video is for the entire set of repertoire routines."},
      { id: "the_toolbar", title: "Results - Toolbar", text : "Shows the results of the import. You can select an item from the list and review it."}
    ]
  })

  // Create filepond component
  const FilePond = vueFilePond(
    FilePondPluginFileValidateType,
    FilePondPluginImagePreview,
    FilePondPluginFileMetadata
  );
  
  setOptions({
      fileMetadataObject: {
          'cookie': connectionStore.getCookie,
          'dummy': connectionStore.getDummy
      }
  })
  
  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    if (connectionStore.getIsSubscriber != 1)
      showState.value = SHOW_SUBSCRIBERS_ONLY
    menuTrigger.value++
  }

  function handleFilePondInit() {
        //console.log("FilePond has initialized");
  
        // FilePond instance methods are available on `this.$refs.pond`
      }
  
  // Chessvis functions

  function onClickImport(index) {
    //console.log("click", index)
    downloadID.value = importHistory.value[index].id

    //console.log("id for rep manager ",downloadID.value)

    showState.value = SHOW_REPMANAGER
  }
  
  function toolbarClick(arrayIndex) {

    // trap that the button should be disabled but the screen hasn't caught up
    if (toolbarElements.value[arrayIndex].disabled)
      return

    switch (arrayIndex) {
      case TOOLBAR_RESULTS :
        toolbarElements.value[TOOLBAR_RESULTS].disabled = true
        toolbarElements.value[TOOLBAR_IMPORT].disabled = false
        showState.value = SHOW_RESULTS
        getImportHistory()
        break
      case TOOLBAR_IMPORT :
        toolbarElements.value[TOOLBAR_RESULTS].disabled = false
        toolbarElements.value[TOOLBAR_IMPORT].disabled = true
        showState.value = SHOW_IMPORT
        break
      case TOOLBAR_TOUR :
        showTour.value = true
        tourTrigger.value++
        break
      case TOOLBAR_DONE :
        router.back()
        break;
    }
    toolbarTrigger.value++

  }

  function tourDone() {
    showTour.value = false
  }

  function repertoireManagerDone() {
    showState.value = SHOW_RESULTS
  }
  
  function processJsonResponse (json) {
    //console.log(" in callback",json)
    if (json.hasOwnProperty('error')) {
      alert("ERROR while running -> " + json['programRun'] + "\n\n" + json['error'])
      return;
    }
   
    const downloadCount = json['download_count']
    const ids = json['id'].split("\t")
    const gameCounts = json['game_count'].split("\t")
    const dates = json['date'].split("\t")
    const sources = json['source'].split("\t")
    const filenames = json['filename'].split("\t")
  
    importHistory.value = []
    for (var i=0; i < downloadCount; i++) {
      const oneImport = { index: i, id: ids[i], count : gameCounts[i], date : dates[i], 
                        source : sources[i], filename : filenames[i]}
      importHistory.value.push(oneImport)
    }
  }
  
  function getImportHistory() {
    var formData = {
      time : Math.floor(Date.now() / 1000)
    }
    //console.log("before post -> ",formData)
    useGetData("chessvis_legacy_repertoire_import_history.php", formData, processJsonResponse)
  }

  // Taken from RepTreeView

  const slotId = ref("edit")

  const COMMENT_FONTSIZE = 16
  const COMMENT_HEIGHT = COMMENT_FONTSIZE * 1.2 * 2 + 4
  
  const commentString = ref("Retrieving repertoire")

  var directionTriggerCounter = 1
  const directionTrigger = ref(1)
  const reloadTrigger = ref(0)

  const repertoireName = ref("")
  var blankAppendTrigger = " "
  const orientation = ref("white")
  const fen = ref(INITIAL_FEN)
  const arrowPairs = ref([])


function toolbarUpdate(backDisabled,forwardDisabled) {
    //console.log("toolbar update from reptree ",backDisabled,forwardDisabled)
  }
  
  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

function repertoireInfo(playingColorName,repertoireName) {
  //console.log("repertoire info coming back",playingColorName,repertoireName)
  commentString.value = repertoireName

  incrementAppendTrigger()
  orientation.value = playingColorName + blankAppendTrigger
}

watch(() => windowSizeStore.getWindowSize, () => {
  //console.log('*********** REP TREE -- window size changes detected')
  windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                    height : windowSizeStore.getWindowSize['height'] }
  //console.log("*** REP TREE new window size",windowSizeRef.value)
  menuTrigger.value++
})

  </script>
  
  <style>

.import-history-container {
  position : relative;
  border-style : solid;
  border-width: 2px;
  margin : 20px 30px;
  margin-top : 75px;
  padding : 15px 15px;
  line-height: 1.4em;
  height: calc(100vh - 150px);
  overflow-y: auto;
}

.import-history-heading {
  background: #4c6ca0;
  color : white;
}

.import-history-row {
  line-height: 1.5em;
}
.import-history-row:hover {
}

/* https://stackoverflow.com/questions/46551925/vuejs-v-bindstyle-hover */
.light-hover-mode:hover {
  background: #d3dfe1;
}

.dark-hover-mode:hover {
  background: #286a6c ;
}

.import-history-source {
  display: inline-block;
  width : 10%;
  bottom : 3px;
  position : relative;
  text-align: left;
}

.import-history-text {
  display: inline-block;
  bottom : 3px;
  position : relative;
}
.import-history-left {
  width : 39%;
}
.import-history-count {
  text-align: right;
  width : 10%;
}

#filepond {
  position : relative;
  border-style : solid;
  border-width: 2px;
  margin : 30px auto;
  margin-top : 75px;
  padding : 15px 15px;
  line-height: 1.4em;
  width : 500px;
  height: calc(100vh - 175px);
  overflow-y: auto;
}

h3 {
  margin-bottom : 10px;
}

#reptree-container {
  position : relative;
  line-height: 1.4em;
}

  </style>
  